import { ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  chakra,
  Spacer,
  Container,
  useToken,
  Heading,
  Image,
  Text,
  Grid,
  ButtonGroup,
  Accordion,
  AccordionIcon,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  useBreakpointValue,
} from "@chakra-ui/react";
import { createContext, useContext, useEffect } from "react";
import { InstantSearch } from "react-instantsearch-hooks";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import PageInfo from "@/components/PageInfo";
import { CircleBorderWrapper } from "@/components/profile/CollegeImageList";
import ProfilePic from "@/components/profile/ProfilePic";
import SimpleTable from "@/components/profile/view/SimpleTable";
import {
  CollapsibleFilter,
  FilterSection,
  Hits,
  RefinementList,
  Pagination,
  SearchableRefinementList,
  Toggle,
} from "@/components/SearchTools";
import { DeprecatedSkewedContainer, LinkButton } from "@/components/shared/generic";
import OptimizedImage from "@/components/shared/OptimizedImage";
import { getEssayExamplesProps } from "@/utils/admin/ssr/essay-examples";
import searchClient, { interceptSearchRequests } from "@/utils/algolia-init";
import { withUserData } from "@/contexts/UserDataContext";
import PageProtector from "@/components/profile/view/PageProtector";

const CollegeContext = createContext();

const useCollege = () => useContext(CollegeContext);

const titleFormats = [
  "[n] Essays that Worked at [college]",
  "[n] Successful [college] Essays",
  "[n] [college] Essays That Worked",
  "[n] [college] Essay Examples",
  "[n] [college] Essay Samples",
  "[n] [college] Essay Examples That Worked",
  "[n] [college] Essay Samples That Worked",
  "[n] [college] Essays That Got In",
  "[n] Essays That Got In to [college]",
];

const Hero = () => {
  const { shortName, name, bannerImage, nEssays, cycle, index } = useCollege();

  const titleFormat = titleFormats[index % titleFormats.length];

  const title = titleFormat?.replace("[n]", nEssays).replace("[college]", shortName);

  return (
    <>
      <PageInfo title={title} />

      <Flex w="100vw" h="100vh" pos="relative" direction="column">
        <OptimizedImage
          zIndex={-2}
          filter="sepia(0.75) saturate(2)"
          src={bannerImage}
          alt={name}
          layout="fill"
          priority
          objectFit="cover"
        />
        {/* vignette effect from top down */}
        <Box
          zIndex={-1}
          pos="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          bg="linear-gradient(to top, transparent 0%, #00000066 30%, #00000088 100%)"
        />
        <Navbar inverted />

        <Spacer />

        <Container color="white" textAlign="center">
          <chakra.h1 textStyle="pageHeader">{title}</chakra.h1>
          <Text mt={4} fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">
            Updated for the {cycle} admissions cycle.
          </Text>
        </Container>

        <Box flex={2} />
      </Flex>
    </>
  );
};

const OverviewSection = () => {
  const {
    shortName,
    image,
    overview,
    acceptanceRate,
    traditions,
    programs,
    avgNetPrice,
    city,
    state,
    tuitionOutOfState,
    satAvg,
    actAvg,
  } = useCollege();
  const borderSize = useBreakpointValue({ base: "1", md: "4" });
  const beige = useToken("colors", "beige");

  return (
    <DeprecatedSkewedContainer
      zIndex={1}
      angle={4}
      mt={-44}
      minH={96}
      containerProps={{ maxW: "container.xl" }}
      pt={40}
      pb={8}
      bg={`linear-gradient(#000000 ${borderSize}%, ${beige} ${borderSize}%, ${beige} ${
        100 - borderSize
      }%, #000000 ${100 - borderSize}%)`}
    >
      <Flex flexDir={{ md: "row", base: "column-reverse" }} gap={20}>
        <Box flex={2}>
          <Heading as="h2" mb={4} fontSize={{ base: "4xl", md: "6xl" }}>
            <Flex align="center" gap={4} fontFamily="heading">
              <CircleBorderWrapper circular circleBorderColor="black" size="1em">
                <Image src={image} display="inline" boxSize="1em" />
              </CircleBorderWrapper>
              About {shortName}
              <Spacer />
            </Flex>
          </Heading>
          <Text mt={4} fontSize="xl">
            {overview}
          </Text>

          <Accordion allowMultiple mt={4}>
            <AccordionItem borderTopWidth={0}>
              <AccordionButton>
                <Heading as="h3" flex="1" textAlign="left" fontWeight="bold" fontSize="xl">
                  Unique traditions at {shortName}
                </Heading>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <Text
                  fontSize="xl"
                  dangerouslySetInnerHTML={{
                    __html: traditions?.replace(/\n/g, "<br/>"),
                  }}
                />
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem borderTopWidth={0} borderBottomColor="transparent">
              <AccordionButton>
                <Heading as="h3" flex="1" fontWeight="bold" textAlign="left" fontSize="xl">
                  Programs at {shortName}
                </Heading>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <Text
                  fontSize="xl"
                  dangerouslySetInnerHTML={{
                    __html: programs?.replace(/\n/g, "<br/>"),
                  }}
                />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>
        <Box>
          <SimpleTable
            data={{
              "Acceptance Rate": acceptanceRate,
              Tuition: tuitionOutOfState,
              "Average Cost": avgNetPrice,
              "Average SAT": satAvg,
              "Average ACT": actAvg,
              Location: `${city}, ${state}`,
            }}
            border="0.2rem dashed black"
            borderRadius="xl"
          >
            <Heading as="h2" mb={4} fontSize="3xl" fontFamily="heading">
              At a glance&hellip;
            </Heading>
            <Box />
          </SimpleTable>
        </Box>
      </Flex>
    </DeprecatedSkewedContainer>
  );
};

const SearchableProfileExamples = () => {
  const { name, shortName, image } = useCollege();

  useEffect(() => {
    interceptSearchRequests(async (requests) => {
      // Hide profiles that aren't ready yet (have show_in_search_results set to false)
      for (const req of requests) {
        if (Array.isArray(req.params.facets)) {
          req.params.facets = req.params.facets.filter((e) => e !== "show_in_search_results");
          req.params.facetFilters =
            req.params.facetFilters?.filter((e) => !e[0].startsWith("show_in_search_results")) ??
            [];

          req.params.facets.push("show_in_search_results");
          req.params.facetFilters.push(["show_in_search_results:true"]);
          req.params.facets.push("accepted_colleges");
          req.params.facetFilters.push([`accepted_colleges:${name}`]);
        }
      }
    });
  }, []);

  return (
    <Box bg="beige" pb={100}>
      <Container py={20} maxW="container.xl">
        <Heading
          as="h2"
          mb={4}
          textAlign="center"
          fontFamily="heading"
          fontSize={{ base: "3xl", md: "5xl" }}
        >
          <Flex align="center" justify="center" textAlign="center" gap={4}>
            Find an essay from your twin at {shortName}{" "}
            <CircleBorderWrapper circular circleBorderColor="black" size="1em">
              <Image src={image} display="inline" boxSize="1em" />
            </CircleBorderWrapper>
          </Flex>
        </Heading>
        <Text mt={4} fontSize={{ base: "lg", md: "2xl" }} textAlign="center">
          Someone with the same interests, stats, and background as you
        </Text>
        <InstantSearch searchClient={searchClient} indexName="admityogi_users">
          <FilterSection>
            <Grid
              bg="white"
              border="0.2rem dashed black"
              borderRadius="xl"
              p={4}
              templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
              overflow="visible"
              gap={4}
              my={10}
            >
              <CollapsibleFilter header="Major Interests" color="pink.300">
                <SearchableRefinementList
                  sorted="count"
                  title="Search for a major..."
                  attribute="academic_interests"
                  limit={100}
                />
              </CollapsibleFilter>
              <CollapsibleFilter header="High School Type" color="pink.500">
                <RefinementList margin attribute="highschool_type" />
              </CollapsibleFilter>
              <CollapsibleFilter header="Background" color="pink.700">
                <ButtonGroup isAttached>
                  <Toggle title="Legacy" attribute="is_legacy" />
                  <Toggle title="NOT legacy" attribute="is_not_legacy" />
                </ButtonGroup>
                <Toggle margin title="First-generation" attribute="is_first_gen" />
                <Toggle margin title="Recruited athlete" attribute="is_athlete" />
                <Toggle margin title="Low-income" attribute="is_low_income" />
              </CollapsibleFilter>
              <CollapsibleFilter header="Race" color="pink.900">
                <RefinementList margin attribute="ethnicity" />
              </CollapsibleFilter>
            </Grid>
          </FilterSection>
          <Pagination />
          <Hits />
        </InstantSearch>
      </Container>
    </Box>
  );
};

const Essay = ({ id, display_name, bio, academic_interests, prompt, content }) => {
  return (
    <>
      <Box
        p={4}
        mt={7}
        bg="white"
        border="0.1rem solid black"
        borderLeft="0.3rem solid black"
        borderBottom="0.3rem solid black"
        borderRadius="xl"
      >
        <Heading as="h3" fontSize="2xl">
          Prompt: {prompt}
        </Heading>
        {content.split("\n\n").map((paragraph, i) => (
          <Text key={i} mt={4}>
            {paragraph}
          </Text>
        ))}
        <chakra.hr mt={2} />
        <Flex gap={4} align="center" mt={2} direction={{ base: "column", md: "row" }}>
          <Flex gap={4} align="center" mt={2} direction="row" flex={1}>
            <ProfilePic uid={id} boxSize={14} />
            <Box>
              <Text fontWeight="bold" fontSize="xl">
                Essay by {display_name}
              </Text>
              <Text>{bio}</Text>
            </Box>
          </Flex>
          <LinkButton
            href={`/profile/${id}`}
            alignSelf="end"
            colorScheme="red"
            rightIcon={<ChevronRightIcon />}
          >
            Read {display_name}&apos;s other essays
          </LinkButton>
        </Flex>
      </Box>
    </>
  );
};

const EssaysSection = () => {
  const { shortName, essays } = useCollege();

  const desktopNotebookDecorations = useBreakpointValue({
    base: "",
    md:
      "url(/images/icons/circle.png) 7.35rem 0 / 1.5rem 36rem repeat-y, " +
      "linear-gradient(to right, red 1px, transparent 1px) 10rem 0rem / 100% 1px, " +
      "linear-gradient(to right, red 1px, transparent 1px) 10.25rem 0rem / 100% 1px, ",
  });

  return (
    <PageProtector>
      <Box
        bgColor="red.50"
        pb={20}
        borderBottom="1.5rem solid black"
        sx={{
          background:
            desktopNotebookDecorations +
            "linear-gradient(to bottom, #00a3e355 1px, transparent 1px) 0 0 / 100% 1.5rem repeat-y",
        }}
      >
        <Container pt={{ base: 20, md: 40 }}>
          <Heading
            as="h2"
            mb={4}
            fontSize={{ base: "5xl", md: "6xl" }}
            fontFamily="heading"
            textAlign="center"
          >
            Real Essays from {shortName} Admits
          </Heading>

          {essays?.map((essay, i) => (
            <Essay {...essay} key={i} />
          ))}
        </Container>
      </Box>
    </PageProtector>
  );
};

const EssayExamplesPage = () => {
  return (
    <>
      <Hero />
      <OverviewSection />
      <EssaysSection />
      <SearchableProfileExamples />

      <Box mt={-100}>
        <Footer />
      </Box>
    </>
  );
};

function ContextWrapper(props) {
  return (
    <CollegeContext.Provider value={props}>
      <EssayExamplesPage />
    </CollegeContext.Provider>
  );
}

export default withUserData(ContextWrapper);

export const getStaticProps = async (ctx) => {
  return {
    props: await getEssayExamplesProps(ctx.params.slug),
  };
};

export const getStaticPaths = async () => {
  const colleges = require("@/utils/data/college-info.json");
  const currentlyAllowedColleges = colleges.slice(
    0,
    process.env.VERCEL_ENV && process.env.NEXT_PUBLIC_GOAL !== "stage" ? 12 : 0,
  );
  const paths = currentlyAllowedColleges
    .filter((college) => !!college.slug)
    .map(({ slug }) => ({ params: { slug } }));
  return {
    paths: paths,
    fallback: false,
  };
};
